import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiClient } from '@/lib/api/client';
import { useAuthStore } from '@/lib/auth/store';
import { ensureArray } from '@/lib/utils/ensure-array';
import type {
  SmsWebhookCreateRequest,
  SmsWebhookDetail,
  SmsWebhookRow,
  SmsWebhookUpdateRequest,
  VoiceWebhookCreateRequest,
  VoiceWebhookDetail,
  VoiceWebhookEventRow,
  VoiceWebhookRow,
  VoiceWebhookUpdateRequest,
  WebhookParameterRow,
} from './types';

export const webhookKeys = {
  all: ['webhooks'] as const,
  voiceList: (userId: string | number | null) =>
    ['webhooks', 'voice', 'list', userId] as const,
  voiceDetail: (id: string | number) =>
    ['webhooks', 'voice', 'detail', id] as const,
  voiceEvents: ['webhooks', 'voice', 'events'] as const,
  voiceBodyParams: ['webhooks', 'voice', 'body-params'] as const,
  voiceHeaderParams: ['webhooks', 'voice', 'header-params'] as const,
  smsList: (userId: string | number | null) =>
    ['webhooks', 'sms', 'list', userId] as const,
  smsDetail: (id: string | number) => ['webhooks', 'sms', 'detail', id] as const,
};

function useInvalidateWebhooks() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: webhookKeys.all });
}

/* ---------------- Voice webhooks ---------------- */

/** GET /api/obd/webhooks/{userId} */
export function useVoiceWebhookList() {
  const userId = useAuthStore((s) => s.userId);
  return useQuery({
    queryKey: webhookKeys.voiceList(userId),
    queryFn: () => api.get<VoiceWebhookRow[]>(`/api/obd/webhooks/${userId}`),
    enabled: Boolean(userId),
    staleTime: 30_000,
    select: (data) => ensureArray<VoiceWebhookRow>(data),
  });
}

/** GET /api/obd/getwebhook/{id} */
export function useVoiceWebhookDetail(id: string | number | null) {
  return useQuery({
    queryKey: webhookKeys.voiceDetail(id ?? 'none'),
    queryFn: () => api.get<VoiceWebhookDetail>(`/api/obd/getwebhook/${id}`),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useVoiceWebhookEvents() {
  return useQuery({
    queryKey: webhookKeys.voiceEvents,
    queryFn: () =>
      api.get<VoiceWebhookEventRow[]>('/api/obd/webhooks/events'),
    staleTime: 5 * 60_000,
    select: (data) => ensureArray<VoiceWebhookEventRow>(data),
  });
}

export function useVoiceWebhookBodyParameters() {
  return useQuery({
    queryKey: webhookKeys.voiceBodyParams,
    queryFn: () =>
      api.get<WebhookParameterRow[]>('/api/obd/webhook/body/parameters'),
    staleTime: 5 * 60_000,
    select: (data) => ensureArray<WebhookParameterRow>(data),
  });
}

export function useVoiceWebhookHeaderParameters() {
  return useQuery({
    queryKey: webhookKeys.voiceHeaderParams,
    queryFn: () =>
      api.get<WebhookParameterRow[]>('/api/obd/webhook/header/parameters'),
    staleTime: 5 * 60_000,
    select: (data) => ensureArray<WebhookParameterRow>(data),
  });
}

export function useCreateVoiceWebhook() {
  const invalidate = useInvalidateWebhooks();
  return useMutation({
    mutationFn: (req: VoiceWebhookCreateRequest) =>
      api.post<{ message?: string }>('/api/obd/addWebHooks', req),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateVoiceWebhook() {
  const invalidate = useInvalidateWebhooks();
  return useMutation({
    mutationFn: (req: VoiceWebhookUpdateRequest) =>
      api.post<{ message?: string }>('/api/obd/webhooks/edit', req),
    onSuccess: () => invalidate(),
  });
}

export function useDeleteVoiceWebhook() {
  const invalidate = useInvalidateWebhooks();
  return useMutation({
    mutationFn: (id: number) =>
      apiClient
        .delete<{ message?: string }>(`/api/obd/webhooks/${id}`)
        .then((r) => r.data),
    onSuccess: () => invalidate(),
  });
}

/* ---------------- SMS webhooks ---------------- */

/** GET /api/obd/sms/webhooks/{userId} */
export function useSmsWebhookList() {
  const userId = useAuthStore((s) => s.userId);
  return useQuery({
    queryKey: webhookKeys.smsList(userId),
    queryFn: () => api.get<SmsWebhookRow[]>(`/api/obd/sms/webhooks/${userId}`),
    enabled: Boolean(userId),
    staleTime: 30_000,
    select: (data) => ensureArray<SmsWebhookRow>(data),
  });
}

/** GET /api/obd/getSMS/webhook/{id} */
export function useSmsWebhookDetail(id: string | number | null) {
  return useQuery({
    queryKey: webhookKeys.smsDetail(id ?? 'none'),
    queryFn: () => api.get<SmsWebhookDetail>(`/api/obd/getSMS/webhook/${id}`),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useCreateSmsWebhook() {
  const invalidate = useInvalidateWebhooks();
  return useMutation({
    mutationFn: (req: SmsWebhookCreateRequest) =>
      api.post<{ message?: string }>('/api/obd/add/sms/webhooks', req),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateSmsWebhook() {
  const invalidate = useInvalidateWebhooks();
  return useMutation({
    mutationFn: (req: SmsWebhookUpdateRequest) =>
      api.post<{ message?: string }>('/api/obd/sms/webhooks/edit', req),
    onSuccess: () => invalidate(),
  });
}

export function useDeleteSmsWebhook() {
  const invalidate = useInvalidateWebhooks();
  return useMutation({
    mutationFn: (id: number) =>
      apiClient
        .delete<{ message?: string }>(`/api/obd/sms/webhooks/${id}`)
        .then((r) => r.data),
    onSuccess: () => invalidate(),
  });
}
